import { Box, Button, Chip, CircularProgress, Divider, Typography, useTheme } from "@mui/material";
import {
  addCardQuantity,
  clearBets,
  clearHistory,
  doubleBets,
  IRequestBody,
  rebet,
  redoBets,
  removeCard,
  setLastBet,
  setPlacedRows,
  undoBets,
} from "../../stores/cardsStore/CardsStore";
import { useCardstore } from "../../stores/cardsStore/useCardsStore";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStatusStore } from "../../stores/statusStore/useStatusStore";
import { useQueryClient } from "@tanstack/react-query";
import { YantraExecutionInfo } from "../../tanstack/queries/player";
import { FormattedNumber, useIntl } from "react-intl";
import { useTranslate } from "../../hooks/useTranslate";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import ColumnSelectButtons from "./columnSelectButtons";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useBuyExecutionMutation } from "../../tanstack/hooks/usePlayerQuery";
import { useSearchParams } from "react-router";
import { useSnackbar } from "notistack";
import { rowsConfiguration } from "../../utils/transformDataToColumns";
import { TranslationKey } from "../../hooks/useLocale";
import { AxiosError } from "axios";
import QuantityCell from "./QuantityCell";

function ControlBoard() {
  const { historyIndex, history, cardIds, yantrasColumns, requestBody } = useCardstore();
  const { isDisabled, activeExecutionId } = useStatusStore();
  const queryClient = useQueryClient();
  const { t } = useTranslate();
  const { formatNumber } = useIntl();
  const executionData = queryClient.getQueryData<{
    yantraExecution: YantraExecutionInfo;
  }>(["execution", activeExecutionId]);
  const theme = useTheme();
  const buyMutation = useBuyExecutionMutation();
  const store = useStatusStore();
  const [params] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [betPlaced, setBetPlaced] = useState(false);
  const [bets, setBets] = useState<Record<string, any>[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openMenuRow, setOpenMenuRow] = useState<string | null>(null);

  const isCardsStateEmpty = useMemo(() => Object.keys(cardIds || {}).length === 0, [cardIds]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    if (openMenuRow === rowId) {
      setAnchorEl(null);
      setOpenMenuRow(null);
    } else {
      setAnchorEl(event.currentTarget);
      setOpenMenuRow(rowId);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuRow(null);
  };

  useEffect(() => {
    setBets([]);
  }, [yantrasColumns]);

  useEffect(() => {
    setPlacedRows(bets.filter((bet) => bet.type === "columnPlaced").map((bet) => bet.bet.toString()));
  }, [bets]);

  const getPlacedBets = (
    tableData: {
      bet: string;
      type: string;
      price: number;
      quantity: unknown;
      total: number;
    }[]
  ) => {
    const transformedData = tableData.map((item) => {
      if (item.type === "image") {
        return { ...item, type: "imagePlaced" };
      }
      if (item.type === "column") {
        return { ...item, type: "columnPlaced" };
      }
      return item;
    });

    setBets((prev) => [...prev, ...transformedData]);
  };

  const saveLastBet = useCallback((executionData: { yantraExecution: YantraExecutionInfo } | undefined, requestBody: IRequestBody) => {
    if (!executionData || !requestBody) return;

    const { yantraId } = executionData.yantraExecution;
    setLastBet(requestBody);

    let bets: Array<{ yantraID: string; lastBet: IRequestBody }> = [];

    try {
      bets = JSON.parse(localStorage.getItem("last-bets-yantra") || "[]");
    } catch (error) {
      console.error("Error parsing localStorage", error);
    }

    const existingBetIndex = bets.findIndex((bet) => bet.yantraID === yantraId);

    if (existingBetIndex >= 0) {
      bets[existingBetIndex].lastBet = requestBody;
    } else {
      bets.push({ yantraID: yantraId, lastBet: requestBody });
    }

    localStorage.setItem("last-bets-yantra", JSON.stringify(bets));
  }, []);

  const placeBet = () => {
    if (cardIds && Object.keys(cardIds).length > 0) {
      setBetPlaced(true);
      buyMutation.mutate(
        {
          executionId: store.activeExecutionId,
          sessionId: params.get("pyramidsid") || "",
          tickets: requestBody || { images: {}, rows: {} },
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("notifications.betsPlaced"), {
              variant: "success",
            });

            getPlacedBets(tableData);
            saveLastBet(executionData, requestBody);
            clearBets();
            clearHistory();
          },
          onError: (error) => {
            if (error instanceof AxiosError && error.response?.data.message.includes("Insufficient funds")) {
              enqueueSnackbar(t("notifications.errorPlacingBetsBalance"), {
                variant: "error",
              });

              return;
            }

            enqueueSnackbar(t("notifications.errorPlacingBets"), {
              variant: "error",
            });
          },
          onSettled: () => {
            setBetPlaced(false);
          },
        }
      );
    }
  };

  const getYantraName = useCallback(
    (id: string) => {
      let name;
      for (const column of yantrasColumns) {
        const card = column.cards.find((card) => card.id === id);
        if (card) {
          name = card.name;
          return name;
        }
      }
      return name || id;
    },
    [yantrasColumns]
  );

  const tableData = useMemo(() => {
    if (!requestBody) return [];

    const imageRows = Object.entries(requestBody.images || {})
      .filter(([_, quantity]) => Number(quantity) > 0)
      .map(([id, quantity]) => ({
        bet: id,
        type: "image",
        price: executionData?.yantraExecution.price || 1,
        quantity,
        total: Number(quantity) * (executionData?.yantraExecution.price || 1),
      }));

    const columnRows = Object.entries(requestBody.rows || {})
      .filter(([_, quantity]) => Number(quantity) > 0)
      .map(([columnIndex, quantity]) => ({
        bet: columnIndex,
        type: "column",
        price: executionData?.yantraExecution.rowPrice || 1,
        quantity,
        total: Number(quantity) * (executionData?.yantraExecution.rowPrice || 1),
      }));

    return [...imageRows, ...columnRows];
  }, [requestBody, executionData]);

  const totalBetsSum = useMemo(() => tableData.reduce((m, v) => m + v.total, 0), [tableData, requestBody, executionData]);

  const handleQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: string, bet: string) => {
      if (type === "imagePlaced" || type === "columnPlaced") return;
      const newValue = parseInt(e.target.value) || 0;

      if (newValue > 0) {
        addCardQuantity(type, bet, newValue);
      }
    },
    [addCardQuantity, yantrasColumns]
  );

  const handleIncrement = useCallback(
    (type: string, bet: string, quantity: number) => {
      if (type === "imagePlaced" || type === "columnPlaced") return;
      addCardQuantity(type, bet, Number(quantity) + 1);
    },
    [addCardQuantity, yantrasColumns]
  );

  const handleDecrement = useCallback(
    (type: string, bet: string, quantity: number) => {
      if (type === "imagePlaced" || type === "columnPlaced") return;
      if (quantity > 1) {
        addCardQuantity(type, bet, Number(quantity) - 1);
      }
    },
    [addCardQuantity, yantrasColumns]
  );

  const handleDelete = useCallback(
    (type: string, bet: string) => {
      if (type === "imagePlaced" || type === "columnPlaced") return;
      if (type === "image") {
        removeCard(type, bet);
      } else {
        removeCard(type, bet);
      }
    },
    [removeCard, yantrasColumns]
  );

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "bet",
        header: t("controlBoard.bet"),
        headerAlign: "center",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }: any) => {
          const { bet, type } = row.original;
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0, sm: 0.5 },
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {type === "image" || type === "imagePlaced"
                ? `${getYantraName(bet)}`
                : t(`colors.${rowsConfiguration[Number(bet)].name}` as TranslationKey)}
              {(type === "imagePlaced" || type === "columnPlaced") && (
                <Chip
                  label={t("controlBoard.placed")}
                  color="success"
                  size={theme.breakpoints.down("sm") ? "small" : "medium"}
                  sx={{
                    color: "#FFFFFF",
                    height: "auto",
                    fontSize: {
                      xs: theme.typography.pxToRem(10),
                      sm: theme.typography.pxToRem(12),
                    },
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        header: t("controlBoard.price"),
        accessorKey: "price",
        headerAlign: "center",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => <FormattedNumber value={cell.getValue<number>()} minimumFractionDigits={2} />,
      },
      {
        header: t("controlBoard.amount"),
        accessorKey: "quantity",
        headerAlign: "center",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const { bet, quantity, type } = row.original;
          return (
            <QuantityCell
              bet={bet}
              quantity={quantity}
              type={type}
              isDisabled={isDisabled}
              handleDecrement={handleDecrement}
              handleIncrement={handleIncrement}
              handleDelete={handleDelete}
              handleQuantityChange={handleQuantityChange}
            />
          );
        },
      },
      {
        header: t("controlBoard.total"),
        accessorKey: "total",
        headerAlign: "center",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => <FormattedNumber value={cell.getValue<number>()} minimumFractionDigits={2} />,
      },
    ],
    [
      t,
      isDisabled,
      anchorEl,
      openMenuRow,
      handleMenuClick,
      handleMenuClose,
      handleDecrement,
      handleIncrement,
      handleDelete,
      handleQuantityChange,
      theme,
    ]
  );

  const table = useMaterialReactTable({
    columns,
    data: [...bets, ...tableData],
    enableColumnActions: false,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: false,
    enableFullScreenToggle: false,
    enableColumnVirtualization: false,
    enableRowVirtualization: false,
    enableStickyHeader: false,
    enableStickyFooter: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnResizing: false,

    muiTableContainerProps: {
      sx: {
        overflowX: "hidden",
        // Safari-specific fixes
        "& table": {
          tableLayout: "auto",
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        tableLayout: { xs: "fixed", md: "auto" },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        textAlign: "center",
        padding: 0.5,
        fontSize: {
          xs: theme.typography.pxToRem(12),
          sm: theme.typography.pxToRem(16),
        },

        "& .MuiBadge-root": {
          minWidth: "24px",
        },

        "&:nth-of-type(1)": {
          maxWidth: { xs: "50px", sm: "120px" },
          minWidth: { xs: "50px", sm: "80px" },
        },
        "&:nth-of-type(2)": {
          maxWidth: { xs: "50px", sm: "120px" },
          minWidth: { xs: "50px", sm: "80px" },
        },
        "&:nth-of-type(3)": {
          maxWidth: { xs: "140px", sm: "180px" },
          minWidth: { xs: "140px", sm: "140px" },
        },
        "&:nth-of-type(4)": {
          maxWidth: { xs: "50px", sm: "120px" },
          minWidth: { xs: "50px", sm: "80px" },
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        textAlign: "center",
        padding: 0.5,
        fontSize: {
          xs: theme.typography.pxToRem(12),
          sm: theme.typography.pxToRem(16),
        },
        "&:nth-of-type(1)": {
          maxWidth: { xs: "50px", sm: "120px" },
          minWidth: { xs: "50px", sm: "80px" },
        },
        "&:nth-of-type(2)": {
          maxWidth: { xs: "50px", sm: "120px" },
          minWidth: { xs: "50px", sm: "80px" },
        },
        "&:nth-of-type(3)": {
          maxWidth: { xs: "140px", sm: "180px" },
          minWidth: { xs: "140px", sm: "140px" },
        },
        "&:nth-of-type(4)": {
          maxWidth: { xs: "50px", sm: "120px" },
          minWidth: { xs: "50px", sm: "80px" },
        },
      },
    },

    renderEmptyRowsFallback: () => (
      <Typography
        sx={{
          textAlign: "center",
          py: 2.5,
        }}
      >
        {t("controlBoard.noBets")}
      </Typography>
    ),
  });

  return (
    <Box
      sx={{
        height: "100%",
        mx: { xs: "auto", lg: 0 },
        width: "100%",
      }}
    >
      <Box
        sx={{
          background: "radial-gradient(50.00% 50.00% at 50% 50%,rgb(252, 167, 166) 26.5%,rgb(255, 144, 143) 100%)",
          borderRadius: 3,
          p: { xs: 1, lg: 3 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: { xs: 1, lg: 3 },
          color: "white",
          fontSize: (theme) => theme.typography.pxToRem(30),
        }}
      >
        {/* radio btns */}
        <ColumnSelectButtons />
        <Divider
          sx={{
            width: `calc(100% + ${theme.spacing(2)})`,
            ml: -1,
            height: theme.spacing(0.25),
            backgroundColor: "#966166",
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 1,
            width: "100%",
          }}
        >
          <Button
            disabled={isCardsStateEmpty || isDisabled}
            variant="contained"
            onClick={doubleBets}
            startIcon={<KeyboardDoubleArrowUpIcon />}
            fullWidth
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(16),
                md: theme.typography.pxToRem(20),
              },
            }}
          >
            {t("controlBoard.double")}
          </Button>
          <Button
            disabled={isDisabled || betPlaced}
            variant="contained"
            onClick={rebet}
            startIcon={<RefreshIcon />}
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(16),
                md: theme.typography.pxToRem(20),
              },
            }}
            fullWidth
          >
            {t("controlBoard.rebet")}
          </Button>
          <Button
            disabled={historyIndex === null || historyIndex < 0 || isDisabled}
            variant="contained"
            onClick={undoBets}
            startIcon={<UndoIcon />}
            fullWidth
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(16),
                md: theme.typography.pxToRem(20),
              },
            }}
          >
            {t("controlBoard.undo")}
          </Button>
          <Button
            disabled={historyIndex === history.length - 1 || !history.length || isDisabled}
            variant="contained"
            onClick={redoBets}
            startIcon={<RedoIcon />}
            fullWidth
            sx={{
              fontSize: {
                xs: theme.typography.pxToRem(16),
                md: theme.typography.pxToRem(20),
              },
            }}
          >
            {t("controlBoard.redo")}
          </Button>
          <Button
            disabled={isCardsStateEmpty || isDisabled}
            variant="contained"
            onClick={clearBets}
            startIcon={<ClearAllIcon />}
            sx={{
              gridColumn: "1 / 2",
              fontSize: {
                xs: theme.typography.pxToRem(16),
                md: theme.typography.pxToRem(20),
              },
            }}
            fullWidth
          >
            {t("controlBoard.clear")}
          </Button>
          <Button
            disabled={isCardsStateEmpty || isDisabled || betPlaced}
            variant="contained"
            onClick={placeBet}
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              gridColumn: "2 / 5",
              fontSize: {
                xs: theme.typography.pxToRem(16),
                md: theme.typography.pxToRem(20),
              },
            }}
            fullWidth
            color="success"
          >
            {betPlaced && <CircularProgress sx={{ color: "white" }} />}
            {!betPlaced && isCardsStateEmpty && t("controlBoard.purchaseYantra")}
            {!betPlaced &&
              !isCardsStateEmpty &&
              t("controlBoard.purchaseYantraSum", {
                amount: formatNumber(totalBetsSum, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
              })}
          </Button>
        </Box>

        {/* table */}

        <Box sx={{ overflowY: "auto", maxHeight: theme.spacing(25) }}>
          <MaterialReactTable table={table} />
        </Box>
        {/*  */}
      </Box>
    </Box>
  );
}

export default React.memo(ControlBoard);
